import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Zap, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (!r.ok) setError(r.error || "Login failed");
    else navigate("/");
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* Left form */}
      <div className="flex items-center justify-center px-8 py-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-12">
            <div className="w-8 h-8 bg-[#002FA7] flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-heading font-black text-lg tracking-tight">LEADFORGE AI</div>
          </div>

          <div className="overline mb-3">/ access</div>
          <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tight leading-none mb-3">
            Sign in to your<br/>command center.
          </h1>
          <p className="text-sm text-zinc-600 mb-10">
            Find local businesses. Score them with AI. Close them with voice.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="overline block mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="w-full px-3 py-3 border border-zinc-300 bg-white text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="overline block mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="w-full px-3 py-3 border border-zinc-300 bg-white text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div className="px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-xs" data-testid="login-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white py-3 text-sm font-bold uppercase tracking-widest flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            >
              {loading ? "Signing in…" : "Enter Dashboard"}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>

          <div className="mt-8 text-sm text-zinc-600">
            <span className="font-mono text-xs">/ invite-only</span>
            <br />
            Need access? Ask your workspace admin for an invite link.
          </div>
        </div>
      </div>

      {/* Right banner */}
      <div
        className="hidden lg:block relative bg-zinc-900"
        style={{
          backgroundImage: "url(https://static.prod-images.emergentagent.com/jobs/9510c952-4fb5-4e6b-af24-6b4b298f0f9c/images/691b57bc483dad0b6645e5347b556ce28fc662c0da1146f5e09827c43f5fdb00.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/30 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="overline opacity-80 mb-3" style={{ color: "white" }}>/ outbound machine</div>
          <div className="font-heading text-3xl font-black leading-tight max-w-md">
            From cold lead to closed deal — fully automated by AI.
          </div>
          <div className="mt-6 grid grid-cols-3 gap-4 max-w-md">
            <Metric label="Scraper" value="Scrapling" />
            <Metric label="Brain" value="Claude 4.5" />
            <Metric label="Voice" value="Vapi.ai" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="border-l-2 border-white pl-3">
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-mono text-sm font-bold mt-1">{value}</div>
    </div>
  );
}
